import type { PropsWithChildren } from 'react';
import { useId } from 'react';
import { styled } from 'styled-components';

import { range } from '#src/lib/arrays.ts';
import { absCenter } from '#src/lib/style.ts';
import { svgCoord } from '#src/lib/svg.ts';

type Coordonate = [x: number, y: number];
const getCircleCoords = (progress: number): Coordonate[] => {
  const precisionMultiplier = 3;
  const total = 100;
  return range(Math.ceil(progress * precisionMultiplier)).map(i => {
    const angle =
      (2 * Math.PI * i) / (total * precisionMultiplier) - Math.PI / 2;
    const x = svgCoord(34 + 29 * Math.cos(angle));
    const y = svgCoord(34 + 29 * Math.sin(angle));
    return [+x, +y];
  });
};

const getPathFromCoords = (coords: Coordonate[]): string => {
  const { path } = coords.reduce(
    (obj, [x, y], i) => {
      if (i % 3 === 0) {
        obj.path += 'C ';
      }
      obj.path += `${x} ${y} `;
      return obj;
    },
    { path: 'M 34 5 ' }
  );
  return path;
};

const RelativeRoot = styled.div`
  position: relative;
`;
const AbsoluteChild = styled.div`
  ${absCenter}
`;

type Props = {
  size?: number;
  value: number;
  min?: number;
  max?: number;
};
export const CircularProgressBar = ({
  size = 80,
  value,
  min = 0,
  max = 100,
  children,
}: PropsWithChildren<Props>) => {
  const filterId = useId();
  const linearGradientId = useId();
  const linearGradientId2 = useId();

  const progress = ((value - min) / (max - min)) * 100;
  const path = getPathFromCoords(getCircleCoords(progress));

  return (
    <RelativeRoot>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        style={{ width: size, height: size }}
        viewBox="0 0 68 68"
      >
        <filter
          id={filterId}
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="13.3333"
          width="14.4014"
          x="55.6104"
          y="27.8662"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            in="SourceGraphic"
            in2="BackgroundImageFix"
            mode="normal"
            result="shape"
          />
          <feGaussianBlur
            result="effect1_foregroundBlur_16819_20929"
            stdDeviation="2"
          />
        </filter>
        <linearGradient
          id={linearGradientId}
          gradientUnits="userSpaceOnUse"
          x1="49.4767"
          x2="66.0135"
          y1="34"
          y2="34"
        >
          <stop offset="0" stopColor="#ffb800" />
          <stop offset=".377213" stopColor="#f0ce1d" />
          <stop offset="1" stopColor="#fcff63" />
        </linearGradient>
        <linearGradient
          id={linearGradientId2}
          gradientUnits="userSpaceOnUse"
          x1="62.811"
          x2="62.811"
          y1="31.8662"
          y2="37.1995"
        >
          <stop offset="0" stopColor="#ffb800" stopOpacity="0" />
          <stop offset=".377213" stopColor="#f0ce1d" />
          <stop offset="1" stopColor="#fcff63" />
        </linearGradient>
        <circle cx="34" cy="34" fill="#fff" fillOpacity=".07" r="34" />
        <path
          d="m34.0068 5c16.0206.00003 29.0069 12.9844 29.0069 29s-12.9863 29-29.0069 29-29.0068-12.9843-29.0068-29 12.9862-29 29.0068-29z"
          stroke="#fff"
          strokeOpacity=".07"
          strokeWidth="6"
        />
        <path
          d={path}
          stroke={`url(#${linearGradientId})`}
          strokeLinecap="round"
          strokeWidth="6"
        />
      </svg>
      <AbsoluteChild>{children}</AbsoluteChild>
    </RelativeRoot>
  );
};
